import { ApiResponse } from "@/interfaces/api-response";
import {
  ResourceGrant,
  ResourceGrantAction,
  ResourceGrantEffect,
} from "@/interfaces/resource-grant.interface";
import { User } from "@/interfaces/user.interface";
import apiClient from "@/utils/axios";

type ListApiResponse<T> = {
  data: T[] | { data?: T[]; items?: T[]; results?: T[]; total?: number };
  error?: string | null;
  pagination?: {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
  };
  total?: number | { total?: number };
};

const dataOf = <T>(body: ListApiResponse<T>) => {
  if (Array.isArray(body.data)) return body.data;
  return body.data.data ?? body.data.items ?? body.data.results ?? [];
};

const getMany = async (resourceType: string, resourceId: string) => {
  const response = await apiClient.get<ApiResponse<ResourceGrant[]>>(
    `/${resourceType}/${resourceId}/grants`,
  );
  return response.data.data ?? [];
};

const getAssignableUsers = async () => {
  const response = await apiClient.get<ListApiResponse<User>>("/users", {
    params: { page: 1, size: 1000 },
  });
  return dataOf(response.data);
};

const createOne = (
  resourceType: string,
  resourceId: string,
  payload: {
    granteeId: string;
    action: ResourceGrantAction;
    effect: ResourceGrantEffect;
  },
) => apiClient.post(`/${resourceType}/${resourceId}/grants`, payload);

const deleteOne = (
  resourceType: string,
  resourceId: string,
  granteeId: string,
  action: ResourceGrantAction,
) => apiClient.delete(`/${resourceType}/${resourceId}/grants/${granteeId}/${action}`);

type BulkApplyOperation = {
  resourceType: string;
  resourceId: string;
  granteeId: string;
  action: ResourceGrantAction;
  effect: ResourceGrantEffect;
};

const bulkRead = async (resources: { resourceType: string; resourceId: string }[]): Promise<ResourceGrant[]> => {
  if (resources.length === 0) return [];
  const response = await apiClient.post<{ data: ResourceGrant[]; error: string | null }>(
    "/grants/bulk_read",
    { resources },
  );
  return Array.isArray(response.data.data) ? response.data.data : [];
};

const bulkRevoke = (grantTargets: { resourceType: string; resourceId: string; granteeId: string }[]) =>
  apiClient.post("/grants/bulk_revoke", { grantTargets });

const bulkApply = (operations: BulkApplyOperation[]) =>
  apiClient.post("/grants/bulk_apply", { operations });

const revokeAll = (resourceEndpoint: string, resourceId: string, granteeId: string) =>
  apiClient.delete(`/${resourceEndpoint}/${resourceId}/grants/${granteeId}`);

const resourceGrantApi = {
  getMany,
  getAssignableUsers,
  createOne,
  deleteOne,
  bulkRead,
  bulkRevoke,
  bulkApply,
  revokeAll,
};

export default resourceGrantApi;
